import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Edit from '@mui/icons-material/Edit';
import { LogSectionsList } from './LogSectionsList';
import { EmptyLogSectionsScreen } from './EmptyLogSectionsScreen';
import { FieldPanel, FormHeader, LineBreak, tooltipStyles } from '../../TemplateForm/styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  selectedWorklogsTemplate,
  selectedWorklogsTemplateId,
  worklogsTemplateFields,
} from '../../state';
import {
  AllLogsTemplatesDocument,
  GetVersionsDocument,
  LogSection,
  SectionUnitsEnum,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
} from '@/__generated__/graphql';
import { removeTypename, useSnackBarMessage } from '../../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';

type KpiLogFormConfigProps = {
  structureId: string;
};

export const KpiLogFormConfig = ({ structureId }: KpiLogFormConfigProps) => {
  const worklogsTemplate = useAtomValue(selectedWorklogsTemplate);
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const [fields, setFields] = useAtom(worklogsTemplateFields);

  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [logSections, setLogSections] = useState<LogSection[]>(worklogsTemplate?.sections ?? []);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    setLogSections(worklogsTemplate?.sections ?? []);

    const templateFields = worklogsTemplate?.fields ?? [];
    setFields(templateFields);
  }, [worklogsTemplate, setFields]);

  const [updateWorklogsTemplateSections, { loading: isLoading }] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar('An error occurred while updating worklogs template', 'error');
        return;
      }
      showSnackBar('Worklogs template saved successfully', 'success');
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
      setMode('ReadOnly');
    },
  });

  const handleCancel = useCallback(() => {
    setLogSections(worklogsTemplate?.sections ?? []);
    const templateFields = worklogsTemplate?.fields ?? [];
    setFields(removeTypename(templateFields));

    setMode('ReadOnly');
  }, [worklogsTemplate, setFields]);

  const handleSaveWorklogsTemplate = useCallback(() => {
    updateWorklogsTemplateSections({
      variables: {
        input: {
          structureId,
          templateId: worklogsTemplateId ?? '',
          lastModifiedFor: 'Update Log Form Sections',
          sections: removeTypename(logSections),
          fields: removeTypename(fields),
        },
      },
      refetchQueries: [GetVersionsDocument],
    });
  }, [structureId, worklogsTemplateId, fields, logSections, updateWorklogsTemplateSections]);

  const handleCreateFirstSection = useCallback(() => {
    const firstSection: LogSection = {
      id: generateMongoObjectId(),
      key: '',
      title: 'New Section',
      sectionUnit: SectionUnitsEnum.Hours,
      order: 1,
      defaultOpen: true,
      fieldKeys: [],
    };

    setLogSections([firstSection]);
  }, []);

  if (logSections.length === 0)
    return (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <EmptyLogSectionsScreen onCreateFirstSection={handleCreateFirstSection} />
        </FieldPanel>
      </Box>
    );

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          KPI Configuration
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveWorklogsTemplate}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <Box p={3}>
        <Stack spacing={2}>
          <LogSectionsList
            mode={mode}
            logSections={logSections}
            onChangeLogSections={setLogSections}
          />
        </Stack>
      </Box>
    </Stack>
  );
};
